/**
 * Resolve the user-facing verb key for an exercise based on its
 * skill vocabulary. Two vocabularies ship today:
 *
 *   'basic'      — Year 1. Uses the three foundational verbs
 *                  SQUEEZE / PUSH / RELAX so a beginner learns to
 *                  differentiate contraction from release before
 *                  anything else. This is the default.
 *
 *   'advanced'   — Year 2. Eight named-skill verbs: LOCK / GRIP /
 *                  PULL / TILT / TWIST / DRAW / LIFT / PULSE. The
 *                  goal is to give the woman a living vocabulary of
 *                  her own body over weeks of practice rather than
 *                  counting anonymous squeezes.
 *
 * The exercise's ring colour and animation class still come from
 * `movementMode` (squeeze / push / relax) — this function only
 * chooses the *word* shown, so a "LOCK" inherits the deep-squeeze
 * motion language without any motion code change.
 *
 * An exercise can always override the vocabulary choice by setting
 * `exercise.verbKey` directly; that wins over everything.
 */

import { classifyAlt } from '@/components/training/RingPlayer/targetLayer';
import { SESSION_STATES } from '@/utils/playerEngine';

const DEFAULT_KEYS = {
  squeeze: 'rp_state_squeeze',
  push:    'rp_state_push',
  relax:   'rp_state_release',
};

/**
 * Entry point. Pass in the live player context — sessionState, the
 * exercise, the opaque ctx, the stateName/mode from targetLayer, plus
 * the vocabulary flag and an isPush hint.
 *
 * Returns a locale key (string) — never a raw word. Call t(verbKey,
 * lang) on the result.
 */
export function resolveVerbKey({ exercise, ctx, sessionState, stateName, isPushMode, skillVocab = 'basic' }) {
  if (exercise?.verbKey) return exercise.verbKey;

  const fallbackKey =
    isPushMode ? DEFAULT_KEYS.push
    : (stateName === 'release' ? DEFAULT_KEYS.relax : DEFAULT_KEYS.squeeze);

  if (skillVocab !== 'advanced') return fallbackKey;

  // Active ring mode + exercise attributes drive the advanced word.
  const type = exercise?.type;
  const movement = String(exercise?.movementMode || exercise?.movement || 'squeeze').toLowerCase();
  const layer = Number.isFinite(exercise?.layer) ? exercise.layer : 3;
  const hold = Number.isFinite(exercise?.hold) ? exercise.hold : 0;

  // Pulse — rapid on/off. Word = PULSE unless the exercise is a
  // bear-down pulse (then PUSH stays).
  if (type === 'pulse') {
    return exercise?.push || movement === 'push' ? 'rp_state_push' : 'rp_state_pulse';
  }

  // Alt — two-phase skill. The phase A verb follows the alt's kind;
  // phase B softens to RELAX for press-release and rock-release, or
  // echoes the phase-A verb for directional-engage (both phases are
  // contractions in that kind).
  if (type === 'alt') {
    const kind = classifyAlt(exercise);
    const isPhaseB = ctx?.altPhase === 1;
    if (kind === 'directional-engage') {
      // Front wall / back wall — a gentle tilt between the two walls.
      return 'rp_state_tilt';
    }
    if (kind === 'rock-release') {
      return isPhaseB ? 'rp_state_release' : 'rp_state_lift';
    }
    // press-release default
    return isPhaseB ? 'rp_state_release' : 'rp_state_squeeze';
  }

  // RELEASE state always lands on RELAX regardless of mode — the
  // softening phase after a squeeze or a dedicated relax exercise.
  if (sessionState === SESSION_STATES.RELEASE) return 'rp_state_release';

  // Hold exercises — choose by mode + layer + duration.
  if (movement === 'push' || isPushMode) {
    // Bear-down / gentle push → DRAW IN (Bel's vocabulary for an
    // inward suction-like engagement).
    return 'rp_state_draw';
  }
  if (movement === 'release' || movement === 'relax' || stateName === 'release') {
    return 'rp_state_release';
  }

  // Pure squeeze hold — pick the named skill by depth + duration.
  //   Deep layer (III) + long sustain (≥8s) → LOCK — a fortress-held
  //     contraction at the deepest fibres.
  //   Deep or Central layer + moderate sustain (≥5s) → GRIP — a
  //     firmly held, shorter contraction.
  //   Entry layer (I) → PULL — an inward draw from the opening.
  //   Anything else → the baseline SQUEEZE.
  if (layer === 3 && hold >= 8) return 'rp_state_lock';
  if (layer >= 2 && hold >= 5) return 'rp_state_grip';
  if (layer === 1) return 'rp_state_pull';
  return 'rp_state_squeeze';
}
